/**
 * Live Conversation — the realtime voice experience.
 *
 * ── HONESTLY DISTINCT FROM THE OTHER TWO MODES ─────────────────────────────────
 *
 * Written Chat types. Record & Review records an answer and reviews it
 * afterwards. This is a conversation that talks back and can be interrupted.
 * They are three different things and the UI says so — Record & Review is not
 * renamed, and nothing classic routes through this component.
 *
 * ── MOBILE FIRST ───────────────────────────────────────────────────────────────
 *
 * People practise speaking on a phone. The layout is a single column at every
 * width, the End control stays reachable without scrolling past the transcript,
 * and nothing depends on hover — a hover-only affordance simply does not exist
 * on the device most likely to be used.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useRealtimeConversation } from '../realtime/useRealtimeConversation';
import { ATTEMPT_ERROR_COPY } from '../utils/attemptErrors';

export interface LiveConversationPanelProps {
  wsUrl: string;
  token: string | null | undefined;
  /**
   * Create the spoken attempt this conversation will run in.
   *
   * Called when the user presses Start — the commitment point for a spoken
   * attempt — and never before: choosing the Live Conversation tab creates no
   * session, reserves nothing, asks for no microphone and opens no socket.
   * Resolves to the new session id, or null if the attempt could not be created
   * (the caller has already said why).
   */
  beginAttempt: () => Promise<string | null>;
  personaName: string;
  /** Server capability: engine AND realtime voice. Never a local flag. */
  available: boolean;
  /**
   * The spoken attempt is over — the user pressed End, or the server completed
   * it. Called EXACTLY ONCE per attempt, with that attempt's session id, after
   * the microphone and socket are released. This is where the caller finalizes
   * the attempt (canonical finalize) and shows its result: a spoken
   * conversation that ends without it is never scored (the server's lifecycle
   * sweeper is only a backstop).
   */
  onEnd?: (sessionId: string) => void;
}

/** Plain words. Provider ids, response refs and sample counts stay internal. */
const STATE_LABEL: Record<string, string> = {
  idle: 'Ready to start',
  connecting: 'Connecting…',
  ready: 'Ready',
  listening: 'Listening',
  thinking: 'Thinking…',
  speaking: 'Speaking',
  reconnecting: 'Reconnecting…',
  ending: 'Finishing…',
  completed: 'Conversation ended',
  error: 'Something went wrong',
};

const ERROR_MESSAGE: Record<string, string> = {
  MIC_DENIED: 'Microphone access was blocked. Allow it in your browser settings, or use Record & Review instead.',
  MIC_UNAVAILABLE: 'No microphone was found. You can still use Record & Review or Written Chat.',
  MIC_LOST: 'The microphone disconnected.',
  CONNECTION_LOST: 'The connection dropped and could not be restored.',
  CONNECTION_ERROR: 'There was a connection problem.',
  REALTIME_UNAVAILABLE: 'Live Conversation is temporarily unavailable.',
  // The attempt is live (or being connected) in another connection — another
  // tab, window or server: refused rather than opening a second session.
  // Worded once, in the shared attempt-error map (utils/attemptErrors).
  REALTIME_SESSION_BUSY: ATTEMPT_ERROR_COPY.REALTIME_SESSION_BUSY.message,
  // Access could not be confirmed (Central unreachable): temporary, not a denial.
  CENTRAL_UNAVAILABLE: ATTEMPT_ERROR_COPY.CENTRAL_UNAVAILABLE.message,
  USAGE_AUTHORITY_UNAVAILABLE: ATTEMPT_ERROR_COPY.USAGE_AUTHORITY_UNAVAILABLE.message,
  CONVERSATION_SESSION_BUSY: ATTEMPT_ERROR_COPY.CONVERSATION_SESSION_BUSY.message,
  DEGRADED: 'The connection is struggling, so audio quality may drop.',
  // A protocol complaint about one frame. The server keeps serving, so this is
  // reported rather than fatal — but it must be NAMED. It previously fell
  // through to the generic message, and a real staging failure surfaced only as
  // "Live Conversation ran into a problem", which is undiagnosable.
  BAD_FRAME: 'A message was rejected by the server. The conversation is still running.',
  CONVERSATION_FAILED: 'The conversation could not continue.',
};

export function LiveConversationPanel({
  wsUrl, token, beginAttempt, personaName, available, onEnd,
}: LiveConversationPanelProps) {
  const convo = useRealtimeConversation({ wsUrl, token, enabled: available });
  /** Creating the attempt is a network round trip; one press makes one attempt. */
  const [beginning, setBeginning] = useState(false);
  /**
   * The attempt this panel is running, and whether its end was already
   * reported. One attempt reports one end, however it ended (End pressed, the
   * server completing it, or both racing).
   */
  const attemptRef = useRef<{ id: string; ended: boolean } | null>(null);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  const reportEnd = useCallback(() => {
    const attempt = attemptRef.current;
    if (!attempt || attempt.ended) return;
    attempt.ended = true;
    onEndRef.current?.(attempt.id);
  }, []);

  const handleStart = async () => {
    if (beginning) return;
    setBeginning(true);
    try {
      const id = await beginAttempt();
      if (id) {
        attemptRef.current = { id, ended: false };
        await convo.start(id);
      }
    } finally {
      setBeginning(false);
    }
  };

  const handleEnd = async () => {
    // Release the microphone and say End to the server first, so the attempt
    // is finished before the caller asks for its result.
    await convo.end();
    reportEnd();
  };

  // The server completed the attempt (time limit, its own end): release the
  // microphone — a completed attempt is never reconnected — and report it.
  const { state: convoState, end: convoEnd } = convo;
  useEffect(() => {
    if (convoState !== 'completed') return;
    const attempt = attemptRef.current;
    if (!attempt || attempt.ended) return;
    void convoEnd().then(reportEnd);
  }, [convoState, convoEnd, reportEnd]);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [convo.entries.length, convo.partial]);

  // The parent should not have offered this mode, but a capability can change
  // under a loaded tab — so say so plainly rather than failing on first use.
  if (!available) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        Live Conversation isn’t available right now. Record &amp; Review and Written Chat still are.
      </div>
    );
  }

  const speaking = convo.state === 'speaking';
  const listening = convo.state === 'listening' || convo.state === 'ready';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">Live Conversation</h3>
          <p className="truncate text-sm text-gray-500">Speaking with {personaName}</p>
        </div>
        <span
          className={clsx(
            'rounded-full px-3 py-1 text-xs font-medium',
            speaking ? 'bg-primary-100 text-primary-800'
              : listening ? 'bg-emerald-100 text-emerald-800'
              : convo.state === 'error' ? 'bg-amber-100 text-amber-900'
              : 'bg-gray-100 text-gray-700'
          )}
        >
          {STATE_LABEL[convo.state] ?? convo.state}
        </span>
      </div>

      {convo.micGranted === false && (
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          {ERROR_MESSAGE.MIC_DENIED}
        </p>
      )}

      {convo.error && convo.micGranted !== false && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          {ERROR_MESSAGE[convo.error] ?? 'Live Conversation ran into a problem.'}
          {/*
            The stable code, always. Without it a staging failure is reported as
            "it said something went wrong", and the server-side line that
            explains it cannot be found. It is an opaque identifier — no
            provider text, no credential, no customer data — and it pairs with
            the session id the server logs alongside every rejection.
          */}
          <span className="ml-2 font-mono text-xs text-amber-700">({convo.error})</span>
        </div>
      )}

      <div className="max-h-[45vh] min-h-[8rem] overflow-y-auto rounded-xl border border-gray-200 bg-white p-3">
        {convo.entries.length === 0 && !convo.partial && (
          <p className="text-sm text-gray-400">
            {convo.active ? 'Start talking when you’re ready.' : 'Press Start to begin.'}
          </p>
        )}
        <ul className="flex flex-col gap-3">
          {convo.entries.map((e) => (
            <li
              key={e.id}
              className={clsx('flex', e.from === 'user' ? 'justify-end' : 'justify-start')}
            >
              <span
                className={clsx(
                  'max-w-[85%] rounded-2xl px-3 py-2 text-sm',
                  e.from === 'user' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900'
                )}
              >
                {e.text}
              </span>
            </li>
          ))}
          {/* The live partial is visibly provisional — it is not history yet. */}
          {convo.partial && (
            <li className="flex justify-end">
              <span className="max-w-[85%] rounded-2xl bg-primary-100 px-3 py-2 text-sm italic text-primary-900">
                {convo.partial}
              </span>
            </li>
          )}
        </ul>
        <div ref={endRef} />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        {!convo.active ? (
          <button
            type="button"
            onClick={() => void handleStart()}
            disabled={!token || beginning}
            className="w-full rounded-xl bg-primary-600 px-4 py-3 text-sm font-medium text-white disabled:bg-gray-200 disabled:text-gray-400 sm:w-auto"
          >
            Start Live Conversation
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void handleEnd()}
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-800 sm:w-auto"
          >
            End conversation
          </button>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Just talk — you can interrupt at any time.
      </p>
    </div>
  );
}
